import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Archive } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { TARGET_TIERS, TARGET_STATUS, tierFarge } from "./eventosUtils";
import TargetDrawer from "./TargetDrawer";

const feil = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Noe gikk galt");

const StatusPill = ({ status }) => {
  const s = TARGET_STATUS.find((x) => x.k === status) || TARGET_STATUS[0];
  return (
    <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium" style={{ color: s.dot }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
      {s.label}
    </span>
  );
};

const NyTargetModal = ({ onLagre, onLukk }) => {
  const [form, setForm] = useState({ name: "", title: "", org: "", tier: "A", rationale: "" });
  const [lagrer, setLagrer] = useState(false);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk]);
  const sett = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const lagre = async () => {
    if (!form.name.trim() || lagrer) return;
    setLagrer(true);
    try { await onLagre(form); } finally { setLagrer(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4" data-testid="ny-target-modal">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onLukk} />
      <div className="sak-modal relative w-full max-w-[480px] bg-white rounded-2xl shadow-2xl border border-[#E7E4DB] p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[15px] font-semibold">Nytt speaker-target</h2>
          <button type="button" onClick={onLukk} className="text-neutral-400 hover:text-neutral-800 transition-colors" data-testid="ny-target-lukk">
            <X size={16} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="adm-label col-span-2">
            Navn *
            <input value={form.name} onChange={(e) => sett("name", e.target.value)} className="adm-input" data-testid="ny-target-navn" autoFocus />
          </label>
          <label className="adm-label">
            Rolle/tittel
            <input value={form.title} onChange={(e) => sett("title", e.target.value)} className="adm-input" data-testid="ny-target-tittel" />
          </label>
          <label className="adm-label">
            Organisasjon
            <input value={form.org} onChange={(e) => sett("org", e.target.value)} className="adm-input" data-testid="ny-target-org" />
          </label>
          <label className="adm-label">
            Tier
            <select value={form.tier} onChange={(e) => sett("tier", e.target.value)} className="adm-input" data-testid="ny-target-tier">
              {TARGET_TIERS.map((t) => (
                <option key={t.k} value={t.k}>{t.label} — {t.sub}</option>
              ))}
            </select>
          </label>
          <label className="adm-label col-span-2">
            Hvorfor dette navnet?
            <textarea rows={2} value={form.rationale} onChange={(e) => sett("rationale", e.target.value)} className="adm-input" data-testid="ny-target-rationale" />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button type="button" onClick={onLukk} className="adm-btn-ghost">Avbryt</button>
          <button type="button" onClick={lagre} disabled={!form.name.trim() || lagrer} className="adm-btn" data-testid="ny-target-lagre">
            {lagrer ? "Lagrer …" : "Legg til"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function TargetsView({ members, aktor, onConverted }) {
  const [targets, setTargets] = useState(null);
  const [aapen, setAapen] = useState(null);
  const [nyModal, setNyModal] = useState(false);
  const [visArkiv, setVisArkiv] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/os/targets");
      setTargets(data.targets);
    } catch (e) {
      feil(e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (tid, p) => {
    try {
      const { data: r } = await api.patch(`/admin/os/targets/${tid}`, { ...p, actor: aktor });
      setTargets((prev) => prev.map((t) => (t.id === tid ? r.item : t)));
    } catch (e) {
      feil(e);
    }
  };

  const opprett = async (payload) => {
    try {
      await api.post("/admin/os/targets", { ...payload, actor: aktor });
      setNyModal(false);
      toast.success("Target lagt til");
      await load();
    } catch (e) {
      feil(e);
    }
  };

  const slett = async (tid) => {
    if (!window.confirm("Slette dette targetet permanent?")) return;
    try {
      await api.delete(`/admin/os/targets/${tid}`);
      setAapen(null);
      setTargets((prev) => prev.filter((t) => t.id !== tid));
      toast.success("Target slettet");
    } catch (e) {
      feil(e);
    }
  };

  const konverter = async (t, valg = {}) => {
    try {
      await api.post(`/admin/os/targets/${t.id}/convert`, { actor: aktor, ownerId: valg.ownerId || null, followUp: valg.followUp || null });
      setAapen(null);
      toast.success(`${t.name} er nå i speaker-pipelinen`);
      await load();
      onConverted?.();
    } catch (e) {
      feil(e);
    }
  };

  const aapenTarget = useMemo(() => (targets || []).find((t) => t.id === aapen) || null, [targets, aapen]);

  if (!targets) {
    return <p className="text-center text-xs text-neutral-400 py-16" data-testid="targets-laster">Laster targets …</p>;
  }

  const aktive = targets.filter((t) => !t.archived);
  const arkiverte = targets.filter((t) => t.archived);
  const eierNavn = (id) => (members || []).find((m) => m.id === id)?.name || "—";

  const Rad = ({ t }) => (
    <button
      type="button"
      onClick={() => setAapen(t.id)}
      className="w-full text-left flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-3 border-t border-[#EEEBE2] first:border-t-0 hover:bg-[#FAF9F4] transition-colors"
      data-testid={`target-rad-${t.id}`}
    >
      <span className="text-[13.5px] font-medium text-neutral-900 min-w-[160px]">{t.name}</span>
      <span className="text-[12px] text-neutral-400 flex-1 min-w-[140px] truncate">
        {[t.title, t.org].filter(Boolean).join(" · ") || "—"}
      </span>
      <StatusPill status={t.status} />
      {t.probability != null && <span className="text-[11.5px] tabular-nums text-neutral-500">{Math.round(t.probability)}%</span>}
      <span className="text-[11.5px] text-neutral-400">{eierNavn(t.ownerId)}</span>
      {t.nextAction && (
        <span className="text-[11.5px] text-neutral-500 truncate max-w-[220px]">
          {t.nextAction}{t.nextActionDate ? ` · ${t.nextActionDate}` : ""}
        </span>
      )}
    </button>
  );

  return (
    <div data-testid="targets-view">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <p className="text-[12px] text-neutral-500" data-testid="targets-teller">
          {aktive.length} aktive targets · ambisjonsliste — teller aldri i pipelinen
        </p>
        <span className="ml-auto" />
        <button type="button" onClick={() => setNyModal(true)} className="adm-btn" data-testid="targets-ny-btn">
          <Plus size={14} /> Nytt target
        </button>
      </div>

      <div className="space-y-5">
        {TARGET_TIERS.map((tier) => {
          const gruppe = aktive.filter((t) => t.tier === tier.k);
          return (
            <section key={tier.k} data-testid={`targets-tier-${tier.k}`}>
              <div className="flex items-baseline gap-2.5 mb-2 px-0.5">
                <span
                  className="inline-flex items-center justify-center w-6 h-6 rounded-md text-[12px] font-bold text-white"
                  style={{ background: tierFarge(tier.k) }}
                >
                  {tier.k}
                </span>
                <span className="text-[12px] font-semibold uppercase tracking-wider text-neutral-600">{tier.label}</span>
                <span className="text-[11.5px] text-neutral-400">{tier.sub}</span>
                <span className="text-[11.5px] tabular-nums text-neutral-400 ml-auto">{gruppe.length}</span>
              </div>
              <div className="adm-card overflow-hidden">
                {gruppe.length === 0 ? (
                  <p className="text-[12px] text-neutral-300 px-4 py-3">Ingen targets i denne tieren</p>
                ) : (
                  gruppe.map((t) => <Rad key={t.id} t={t} />)
                )}
              </div>
            </section>
          );
        })}
      </div>

      {arkiverte.length > 0 && (
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setVisArkiv((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[12px] text-neutral-400 hover:text-neutral-700 transition-colors"
            data-testid="targets-arkiv-toggle"
          >
            <Archive size={12} /> {visArkiv ? "Skjul arkiv" : `Vis arkiv (${arkiverte.length})`}
          </button>
          {visArkiv && (
            <div className="adm-card overflow-hidden mt-2 opacity-70" data-testid="targets-arkiv">
              {arkiverte.map((t) => <Rad key={t.id} t={t} />)}
            </div>
          )}
        </div>
      )}

      {aapenTarget && (
        <TargetDrawer
          target={aapenTarget}
          members={members}
          onPatch={(p) => patch(aapenTarget.id, p)}
          onConvert={(valg) => konverter(aapenTarget, valg)}
          onDelete={() => slett(aapenTarget.id)}
          onLukk={() => setAapen(null)}
        />
      )}
      {nyModal && <NyTargetModal onLagre={opprett} onLukk={() => setNyModal(false)} />}
    </div>
  );
}
